import { useState } from 'react';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { Pager } from '@/components/pager';
import { Badge, Callout, EmptyState, buttonClass, inputClass } from '@/components/ui';
import { cn } from '@/lib/cn';
import { xeroChartQuery, xeroStatusQuery, xeroLogQuery, xeroQueueQuery, type XeroStatus } from '@/queries';

/**
 * The Xero bridge, as a screen. Spec 004, FR-X34.
 *
 * Three questions, in the order somebody asks them:
 *
 *   1. Is it connected, and if not, what is missing?
 *   2. Is anything stuck and waiting for me?
 *   3. What has moved, and when?
 *
 * The first is a sentence rather than a green dot, because "not connected" and
 * "connected but refusing to post" are different problems with different fixes
 * and a dot cannot tell them apart.
 */

const OUTCOME_TONE: Record<string, 'ok' | 'warn' | 'danger' | 'neutral'> = {
  ok: 'ok',
  skipped: 'neutral',
  retryable: 'warn',
  terminal: 'danger',
  auth: 'danger',
};

const KIND_LABEL: Record<string, string> = {
  invoice: 'Invoice',
  credit_note: 'Credit note',
  purchase_order: 'Bill',
  // Spec 017: a bill for one goods receipt, and one scheduled run in the log.
  bill: 'Bill',
  contact: 'Contact',
  sync: 'Sync run',
};

function when(value: string | null | undefined): string {
  if (!value) return 'never';
  return new Date(value).toLocaleString('en-AU');
}


/**
 * The account codes, and the switch that lets anything post at all.
 *
 * Until this existed the codes could only be changed in the database, so the
 * one step between a working connection and a working bridge needed a
 * developer. Four gaps of that exact shape turned up this week; a setting the
 * client must own has to be a screen.
 *
 * The codes come from a picker built from Guy's OWN chart of accounts,
 * pulled back from Xero, because the pair sitting in the row today are Xero's
 * demo company defaults and a guessed code puts figures in the wrong place on
 * a BAS for a quarter.
 */
function Settings({ config, chart }: {
  config: XeroStatus['config'];
  chart: { accounts: Array<{ code: string; name: string }>; tax_rates: Array<{ tax_type: string; name: string }>; pulled: boolean } | undefined;
}) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [said, setSaid] = useState<string | null>(null);

  const [sales, setSales] = useState(config?.sales_account_code ?? '');
  const [purchase, setPurchase] = useState(config?.purchase_account_code ?? '');
  const [cogs, setCogs] = useState(config?.cogs_account_code ?? '');
  const [inventory, setInventory] = useState(config?.inventory_account_code ?? '');
  const [salesTax, setSalesTax] = useState(config?.sales_tax_type ?? 'OUTPUT');
  const [purchaseTax, setPurchaseTax] = useState(config?.purchase_tax_type ?? 'INPUT');
  const [days, setDays] = useState(String(config?.invoice_due_days ?? 14));
  const [confirmed, setConfirmed] = useState(config?.codes_are_temporary === false);

  const save = useMutation({
    mutationFn: async () => {
      const res = await fetch('/api/xero/config', {
        method: 'PATCH',
        credentials: 'same-origin',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          sales_account_code: sales,
          purchase_account_code: purchase,
          cogs_account_code: cogs,
          inventory_account_code: inventory,
          sales_tax_type: salesTax,
          purchase_tax_type: purchaseTax,
          invoice_due_days: Number(days),
          confirmed,
        }),
      });
      const body = (await res.json().catch(() => null)) as
        | { message?: string; error?: { message?: string } } | null;
      if (!res.ok) throw new Error(body?.error?.message ?? 'Could not save it.');
      return body?.message ?? 'Saved.';
    },
    onSuccess: (m) => {
      setSaid(m);
      void qc.invalidateQueries({ queryKey: ['admin', 'xero'] });
    },
    onError: (e: Error) => setSaid(e.message),
  });

  const accountField = (
    label: string, hint: string, value: string, set: (v: string) => void, required = false,
  ) => (
    <label className="block">
      <span className="mb-1 block text-[12.5px] font-semibold">{label}</span>
      {chart?.pulled ? (
        <select value={value} onChange={(e) => set(e.target.value)} className={`${inputClass} w-full`}>
          <option value="">{required ? 'Choose a code' : 'Not used'}</option>
          {chart.accounts.map((a) => (
            <option key={a.code} value={a.code}>{a.code} {a.name}</option>
          ))}
        </select>
      ) : (
        <input
          value={value}
          onChange={(e) => set(e.target.value)}
          placeholder="200"
          className={`${inputClass} w-full font-[family-name:var(--font-mono)]`}
        />
      )}
      <span className="mt-1 block text-[12px] text-muted">{hint}</span>
    </label>
  );

  if (!open) {
    return (
      <button type="button" onClick={() => setOpen(true)} className={buttonClass('secondary', 'sm', 'mb-6')}>
        Set the account codes
      </button>
    );
  }

  return (
    <form
      className="mb-6 rounded-[10px] border border-line-strong bg-panel p-4"
      onSubmit={(e) => { e.preventDefault(); save.mutate(); }}
    >
      {!chart?.pulled && (
        <Callout tone="warn" title="The chart of accounts has not been pulled yet" className="mb-4">
          Run <code className="font-[family-name:var(--font-mono)]">npm run xero:settings</code> once
          the connection is live, and these become pickers from Guy's own chart
          instead of boxes to type a number into.
        </Callout>
      )}

      <div className="grid gap-4 sm:grid-cols-2">
        {accountField('Sales', 'Where an invoice posts. Often 200.', sales, setSales, true)}
        {accountField('Purchases', 'Where a supplier bill posts. Often 300 or 310.', purchase, setPurchase, true)}
        {accountField('Cost of goods sold', 'Optional until stock posting is turned on.', cogs, setCogs)}
        {accountField('Inventory on hand', 'Optional until stock posting is turned on.', inventory, setInventory)}

        <label className="block">
          <span className="mb-1 block text-[12.5px] font-semibold">Tax on sales</span>
          {chart?.pulled ? (
            <select value={salesTax} onChange={(e) => setSalesTax(e.target.value)} className={`${inputClass} w-full`}>
              {chart.tax_rates.map((t) => (
                <option key={t.tax_type} value={t.tax_type}>{t.name}</option>
              ))}
            </select>
          ) : (
            <input value={salesTax} onChange={(e) => setSalesTax(e.target.value)} className={`${inputClass} w-full font-[family-name:var(--font-mono)]`} />
          )}
        </label>

        <label className="block">
          <span className="mb-1 block text-[12.5px] font-semibold">Tax on purchases</span>
          {chart?.pulled ? (
            <select value={purchaseTax} onChange={(e) => setPurchaseTax(e.target.value)} className={`${inputClass} w-full`}>
              {chart.tax_rates.map((t) => (
                <option key={t.tax_type} value={t.tax_type}>{t.name}</option>
              ))}
            </select>
          ) : (
            <input value={purchaseTax} onChange={(e) => setPurchaseTax(e.target.value)} className={`${inputClass} w-full font-[family-name:var(--font-mono)]`} />
          )}
        </label>

        <label className="block">
          <span className="mb-1 block text-[12.5px] font-semibold">Invoice terms</span>
          <input
            value={days}
            onChange={(e) => setDays(e.target.value)}
            inputMode="numeric"
            className={`${inputClass} w-full font-[family-name:var(--font-mono)]`}
          />
          <span className="mt-1 block text-[12px] text-muted">
            Days from the invoice date. A customer with their own terms overrides this.
          </span>
        </label>
      </div>

      {/* The real action on this form. Nothing posts while this is unticked. */}
      <label className="mt-4 flex items-start gap-2 text-[13px]">
        <input
          type="checkbox"
          className="mt-1"
          checked={confirmed}
          onChange={(e) => setConfirmed(e.target.checked)}
        />
        <span>
          These are the right codes, checked against the chart of accounts.
          <span className="block text-[12px] text-muted">
            Nothing posts to Xero until this is ticked. A guessed code puts
            figures in the wrong place on a BAS for a quarter.
          </span>
        </span>
      </label>

      {said && (
        <p className="mt-3 rounded-[10px] border border-line p-3 text-[13px]">{said}</p>
      )}

      <div className="mt-4 flex items-center gap-2">
        <button type="submit" disabled={save.isPending} className={buttonClass('primary', 'sm')}>
          {save.isPending ? 'Saving...' : 'Save'}
        </button>
        <button type="button" onClick={() => { setOpen(false); setSaid(null); }} className={buttonClass('secondary', 'sm')}>
          Cancel
        </button>
      </div>
    </form>
  );
}

export default function AdminXeroPage() {
  const qc = useQueryClient();
  const [logPage, setLogPage] = useState(1);
  const [queuePage, setQueuePage] = useState(1);

  const { data: status } = useQuery(xeroStatusQuery);
  const { data: chart } = useQuery(xeroChartQuery);
  const { data: log, isFetching: logBusy } = useQuery({
    ...xeroLogQuery(logPage),
    placeholderData: keepPreviousData,
  });
  const { data: queue, isFetching: queueBusy } = useQuery({
    ...xeroQueueQuery(queuePage),
    placeholderData: keepPreviousData,
  });

  const retry = useMutation({
    mutationFn: async (jobId: string) => {
      const res = await fetch(`/api/xero/jobs/${jobId}/retry`, {
        method: 'POST',
        credentials: 'same-origin',
      });
      if (!res.ok) throw new Error('Could not queue it again.');
      return res.json();
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin', 'xero'] });
    },
  });

  // Spec 017, FR-1721. One document at a time, then "I checked it in Xero".
  const [said, setSaid] = useState<string | null>(null);
  const act = useMutation({
    mutationFn: async ({ jobId, what }: { jobId: string; what: 'send' | 'checked' }) => {
      const res = await fetch(`/api/xero/jobs/${jobId}/${what}`, { method: 'POST', credentials: 'same-origin' });
      const body = (await res.json().catch(() => null)) as { message?: string; error?: { message?: string } } | null;
      if (!res.ok) throw new Error(body?.error?.message ?? 'That did not work. Try again.');
      return body?.message ?? 'Done.';
    },
    onSuccess: (m) => { setSaid(m); void qc.invalidateQueries({ queryKey: ['admin', 'xero'] }); },
    onError: (e: Error) => { setSaid(e.message); void qc.invalidateQueries({ queryKey: ['admin', 'xero'] }); },
  });

  const rows = (log?.rows ?? []) as Array<{
    id: number; direction: string; kind: string; platform_id: string | null;
    outcome: string; http_status: number | null; xero_message: string | null;
    attempt: number; created_at: string;
  }>;

  const jobs = (queue?.rows ?? []) as Array<{
    id: string; kind: string; state: string; attempts: number;
    next_attempt_at: string; last_error: string | null; created_at: string;
    sent_at?: string | null; checked_at?: string | null;
  }>;

  const stuck = jobs.filter((j) => j.state === 'needs_person');
  const waiting = jobs.filter((j) => j.state === 'queued');
  const toCheck = jobs.filter((j) => j.state === 'done' && j.sent_at && !j.checked_at);
  const first = status?.one_at_a_time;
  // Until five are checked, the next one waits for the last one to be checked.
  // The server holds the same rule (`sendOne`).
  const mustCheck = toCheck.length > 0 && !first?.done;

  return (
    <div>
      <div className="mb-[14px]">
        <h1 className="text-[22px] font-semibold tracking-[-0.01em]">Xero</h1>
        <p className="mt-1 max-w-[80ch] text-[12.5px] text-muted">
          An approved order puts its invoice in the queue, and a receipt puts its
          bill in the queue. Nothing goes to Xero until a person sends it from
          this screen. Xero stays the system of record for money.
        </p>
      </div>

      {/* The one thing that stops everything, said in a sentence. */}
      {status?.blocked_reason && (
        <Callout tone="warn" title="Nothing is posting to Xero yet" className="mb-5">
          {status.blocked_reason}
        </Callout>
      )}

      {/*
        * Rendered only once the status has arrived.
        *
        * The form seeds its fields with `useState(config?.x ?? '')`, which runs
        * ONCE on mount. Mounted while the query was still loading, it captured
        * empty strings and never picked the real codes up: the screen showed
        * blank Sales and Purchases boxes over a config that holds 200 and 310,
        * and saving would have been refused for a value that was already
        * there. Waiting for the data is simpler than syncing it afterwards.
        */}
      {status && <Settings config={status.config} chart={chart} />}

      <div className="mb-6 grid gap-px overflow-hidden rounded-[10px] border border-line bg-line sm:grid-cols-2 lg:grid-cols-4">
        <div className="bg-panel px-4 py-4">
          <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-muted">Connection</p>
          <p className="mt-1 text-[15px] font-semibold">
            {status?.configured ? (status.connection?.org_name ?? 'Credentials set') : 'Not connected'}
          </p>
          <p className="mt-0.5 text-[12px] text-muted">
            {status?.configured
              ? 'Client credentials, no sign in needed'
              : 'Client id and secret are not set on the server'}
          </p>
        </div>
        <div className="bg-panel px-4 py-4">
          <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-muted">Last success</p>
          <p className="mt-1 text-[15px] font-semibold">{when(status?.connection?.last_success_at)}</p>
          <p className="mt-0.5 text-[12px] text-muted">
            {status?.connection?.minute_remaining != null
              ? `${status.connection.minute_remaining} calls left this minute`
              : 'Rate limits unknown until the first call'}
          </p>
        </div>
        <div className="bg-panel px-4 py-4">
          <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-muted">Waiting for a person</p>
          <p className={cn('mt-1 text-[15px] font-semibold',
            (status?.needs_person ?? 0) > 0 && 'text-[color:var(--color-danger-ink)]')}>
            {status?.needs_person ?? 0}
          </p>
          <p className="mt-0.5 text-[12px] text-muted">Jobs that will not retry on their own</p>
        </div>
        <div className="bg-panel px-4 py-4">
          <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-muted">Account codes</p>
          <p className="mt-1 text-[15px] font-semibold">
            {status?.config?.codes_are_temporary === false ? 'Confirmed' : 'Placeholders'}
          </p>
          <p className="mt-0.5 text-[12px] text-muted">
            Sales {status?.config?.sales_account_code ?? '?'}, purchases {status?.config?.purchase_account_code ?? '?'}
          </p>
        </div>
      </div>

      <div className="mb-6 grid gap-px overflow-hidden rounded-[10px] border border-line bg-line sm:grid-cols-3">
        <div className="bg-panel px-4 py-4">
          <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-muted">Audit</p>
          <p className="mt-1 text-[15px] font-semibold">
            {status?.audit?.passed ? 'Passed' : 'Not passed'}
          </p>
          <p className="mt-0.5 text-[12px] text-muted">
            {status?.audit?.passed
              ? `On ${when(status.audit.ran_at)}`
              : (status?.audit?.reason ?? 'The audit has not been run.')}
          </p>
        </div>
        <div className="bg-panel px-4 py-4">
          <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-muted">One at a time</p>
          <p className="mt-1 text-[15px] font-semibold">
            {first ? `${Math.min(first.checked, first.needed)} of ${first.needed} checked` : '?'}
          </p>
          <p className="mt-0.5 text-[12px] text-muted">
            {first?.done
              ? 'The scheduled sync can send the rest'
              : 'Send each one, then check it in Xero'}
          </p>
        </div>
        <div className="bg-panel px-4 py-4">
          <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-muted">Schedule</p>
          <p className="mt-1 text-[15px] font-semibold">{status?.schedule_on ? 'On, every 15 minutes' : 'Off'}</p>
          <p className="mt-0.5 text-[12px] text-muted">
            {status?.schedule_on ? 'Set on the server' : 'Only a person sends to Xero'}
          </p>
        </div>
      </div>

      {said && (
        <p role="status" className="mb-5 rounded-[10px] border border-line p-3 text-[13px]">{said}</p>
      )}

      {toCheck.length > 0 && (
        <section className="mb-8">
          <h2 className="mb-2 text-[13px] font-bold uppercase tracking-[0.08em] text-muted">
            Sent, check in Xero ({toCheck.length})
          </h2>
          <div className="overflow-hidden rounded-[10px] border border-line-strong bg-panel">
            {toCheck.map((j) => (
              <div key={j.id} className="flex flex-wrap items-center gap-3 border-b border-line px-4 py-3 last:border-0">
                <span className="min-w-[90px] text-[13px] font-semibold">{KIND_LABEL[j.kind] ?? j.kind}</span>
                <span className="flex-1 text-[13px] text-muted">Sent {when(j.sent_at)}</span>
                <button
                  type="button"
                  className={buttonClass('secondary', 'sm')}
                  disabled={act.isPending}
                  onClick={() => { setSaid(null); act.mutate({ jobId: j.id, what: 'checked' }); }}
                >
                  I checked it in Xero
                </button>
              </div>
            ))}
          </div>
          <p className="mt-2 max-w-[70ch] text-[12.5px] text-muted">
            Open the document in Xero. Compare the contact, the lines, the account
            codes, the tax and the total with the order or the receipt. Then
            select I checked it in Xero.
          </p>
        </section>
      )}

      {waiting.length > 0 && (
        <section className="mb-8">
          <h2 className="mb-2 text-[13px] font-bold uppercase tracking-[0.08em] text-muted">
            Waiting to send ({status?.waiting ?? waiting.length})
          </h2>
          <div className="overflow-hidden rounded-[10px] border border-line-strong bg-panel">
            {waiting.map((j) => (
              <div key={j.id} className="flex flex-wrap items-center gap-3 border-b border-line px-4 py-3 last:border-0">
                <span className="min-w-[90px] text-[13px] font-semibold">{KIND_LABEL[j.kind] ?? j.kind}</span>
                <span className="flex-1 text-[13px] text-muted">Queued {when(j.created_at)}</span>
                <button
                  type="button"
                  className={buttonClass('primary', 'sm')}
                  disabled={act.isPending || mustCheck}
                  title={mustCheck ? 'Check the document you sent first.' : undefined}
                  onClick={() => { setSaid(null); act.mutate({ jobId: j.id, what: 'send' }); }}
                >
                  Send to Xero
                </button>
              </div>
            ))}
          </div>
          {mustCheck && (
            <p className="mt-2 text-[12.5px] text-muted">Check the document you sent in Xero before you send the next one.</p>
          )}
        </section>
      )}

      {stuck.length > 0 && (
        <section className="mb-8">
          <h2 className="mb-2 text-[13px] font-bold uppercase tracking-[0.08em] text-muted">
            Needs a person ({stuck.length})
          </h2>
          <div className="overflow-hidden rounded-[10px] border border-line-strong bg-panel">
            {stuck.map((j) => (
              <div key={j.id} className="flex flex-wrap items-center gap-3 border-b border-line px-4 py-3 last:border-0">
                <span className="min-w-[90px] text-[13px] font-semibold">
                  {KIND_LABEL[j.kind] ?? j.kind}
                </span>
                <span className="flex-1 text-[13px] text-muted">
                  {j.last_error ?? 'No reason recorded.'}
                </span>
                <span className="text-[12px] text-muted">{j.attempts} attempts</span>
                <button
                  type="button"
                  className={buttonClass('secondary', 'sm')}
                  disabled={retry.isPending}
                  onClick={() => retry.mutate(j.id)}
                >
                  Try again
                </button>
              </div>
            ))}
          </div>
          <p className="mt-2 max-w-[70ch] text-[12.5px] text-muted">
            These stopped because Xero refused them for a reason that will not
            change on its own: a rejected account code, a missing scope, or a
            customer with no name. Fix the reason, then try again.
          </p>
        </section>
      )}

      <section>
        <h2 className="mb-2 text-[13px] font-bold uppercase tracking-[0.08em] text-muted">
          What moved
        </h2>

        {rows.length === 0 ? (
          <EmptyState
            title="Nothing has gone across yet"
            body="Every call to Xero is recorded here, in both directions, with
                  whatever Xero said about it. The first entry appears when a
                  person sends the first document."
          />
        ) : (
          <div className="overflow-hidden rounded-[6px] border border-line bg-panel">
            <div className="overflow-x-auto">
              <table className="dt w-full min-w-[760px] text-left">
                <thead>
                  <tr>
                    <th scope="col">When</th>
                    <th scope="col">Way</th>
                    <th scope="col">What</th>
                    <th scope="col">Outcome</th>
                    <th scope="col">Xero said</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.id}>
                      <td className="whitespace-nowrap text-[12.5px] text-muted">{when(r.created_at)}</td>
                      <td className="text-[12.5px] text-muted">{r.direction === 'out' ? 'To Xero' : 'From Xero'}</td>
                      <td className="text-[13px]">{KIND_LABEL[r.kind] ?? r.kind}</td>
                      <td>
                        <Badge tone={OUTCOME_TONE[r.outcome] ?? 'neutral'}>{r.outcome}</Badge>
                        {r.attempt > 1 && (
                          <span className="ml-2 text-[12px] text-muted">attempt {r.attempt}</span>
                        )}
                      </td>
                      <td className="max-w-[420px] text-[12.5px] text-muted">
                        {r.xero_message ?? (r.http_status ? `HTTP ${r.http_status}` : '')}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Pager
              page={log?.page ?? 1}
              pages={log?.pages ?? 1}
              total={log?.total ?? 0}
              limit={log?.limit ?? 50}
              noun="entry"
              plural="entries"
              busy={logBusy || queueBusy}
              onPage={(n) => { setLogPage(n); setQueuePage(1); window.scrollTo({ top: 0 }); }}
            />
          </div>
        )}
      </section>
    </div>
  );
}
